import { HiArrowDown, HiArrowUp } from "react-icons/hi";
import { GrFormSubtract } from "react-icons/gr";

const greenGrowth = <HiArrowUp size={14} style={{ color: "#238654" }} />;
const redGrowth = <HiArrowDown size={14} style={{ color: "#F42C2C" }} />;

export const getUniqueKeywordLocationPairs = (arr) => {
  const set = new Set();
  const result = [];

  arr.forEach((item) => {
    const location = item.location_code;
    item.keyword.forEach((kw) => {
      const key = `${kw.toLowerCase().trim()}|${location}`;
      if (!set.has(key)) {
        set.add(key);
        result.push({
          keyword: kw,
          location: String(location),
          type: "pending",
        });
      }
    });
  });

  return result;
};

export function rankDiff(rank, prevRank, type = "span") {
  // If both are 'No Rank'
  if (rank === 0 && prevRank === 0) return rankDiffColor(0, type);
  // Convert to numbers
  const r = Number(rank);
  const pr = Number(prevRank);

  // If either is not a number
  if (isNaN(r) || isNaN(pr)) rankDiffColor(0, type);
  // If difference is 0
  if (r - pr === 0) return rankDiffColor(0, type);
  // If previous rank is greater than current, return negative diff
  if (pr > r) return rankDiffColor(-(pr - r), type);
  if (r > pr && pr !== 0) return rankDiffColor(-(r - pr), type);
  // Otherwise, return positive diff
  return rankDiffColor(r - pr, type);
}

function rankDiffColor(diff, type) {
  if (type === "value") return diff;
  const diffVal = parseInt(diff, 10);
  let rankStatus = <GrFormSubtract />; // Default
  if (diffVal > 0) rankStatus = greenGrowth;
  else if (diffVal < 0) rankStatus = redGrowth;
  const positiveValue = Math.abs(diff);
  return (
    <span
      className="growArrow"
      style={{
        marginLeft: "2px",
        marginRight: "2px",
        lineHeight: "normal",
      }}
    >
      {rankStatus}
      {positiveValue > 0 && positiveValue}
    </span>
  );
}

export const isNoRank = (rank) => {
  const num = rank === "No Rank" ? 0 : Number(rank);
  return isNaN(num) ? 0 : num;
};

function classifyRankDiff(diff) {
  if (typeof diff === "number") {
    if (diff > 0) return "improved"; // rank got worse (higher number)
    if (diff < 0) return "declined"; // rank improved (lower number)
    if (diff === 0) return "no change";
  }
  return "invalid"; // fallback
}

export function keywordStatus(item) {
  const gptDiff = rankDiff(
    isNoRank(item.gpt_rank),
    isNoRank(item.gpt_prev_rank),
    "value"
  );
  const claudeDiff = rankDiff(
    isNoRank(item.claude_rank),
    isNoRank(item.claude_prev_rank),
    "value"
  );
  const ppDiff = rankDiff(
    isNoRank(item.pp_rank),
    isNoRank(item.pp_prev_rank),
    "value"
  );

  // Classify each diff
  const gptChange = classifyRankDiff(gptDiff);
  const claudeChange = classifyRankDiff(claudeDiff);
  const ppChange = classifyRankDiff(ppDiff);

  // Final OR checks 🔍
  const hasImproved =
    gptChange === "improved" ||
    claudeChange === "improved" ||
    ppChange === "improved";

  const hasDeclined =
    gptChange === "declined" ||
    claudeChange === "declined" ||
    ppChange === "declined";

  const hasNoChange =
    gptChange === "no change" ||
    claudeChange === "no change" ||
    ppChange === "no change";

  if (hasDeclined) return "declined";
  if (hasImproved) return "improved";
  if (hasNoChange) return "no change";
  return "no change";
}

export const avgRanks = (arr) => {
  if (!arr?.length) return [];
  const total = arr?.reduce(
    (acc, item) => {
      acc.gpt += isNoRank(item.gpt_rank);
      acc.claude += isNoRank(item.claude_rank);
      acc.pp += isNoRank(item.pp_rank);
      return acc;
    },
    { gpt: 0, claude: 0, pp: 0 }
  );
  const length = arr.length;
  return {
    avg_gpt_rank: (total.gpt / length).toFixed(2),
    avg_claude_rank: (total.claude / length).toFixed(2),
    avg_pp_rank: (total.pp / length).toFixed(2),
  };
};

export const convertToDomainFormat = (data) => {
  return data.map((item) => {
    const { keyword, location, gpt, claude, perplexity } = item;
    const domainMap = {};

    const addToDomain = (entries, modelIndex) => {
      entries.forEach((obj) => {
        const domain = Object.keys(obj)[0];
        const value = obj[domain];

        if (!domainMap[domain]) domainMap[domain] = [0, 0, 0];
        domainMap[domain][modelIndex] += value;
      });
    };

    addToDomain(gpt, 0); // Index 0 → GPT
    addToDomain(claude, 1); // Index 1 → Claude
    addToDomain(perplexity, 2); // Index 2 → Perplexity

    const result = { keyword, location };

    Object.entries(domainMap).forEach(([domain, [g, c, p]]) => {
      result[domain] = `${g} / ${c} / ${p}`;
    });

    return result;
  });
};

export const tooltip = {
  mention: (
    <div style={{ maxWidth: 300, lineHeight: "1.5em", fontSize: 12 }}>
      <b>Mentions</b>
      <p>
        Mentions represent how many times your brand is referenced in
        AI-generated responses for the selected keyword.
      </p>

      <p>
        <b>Higher mentions</b> = your brand is frequently appearing in answers.
      </p>

      <p>
        Mentions are counted across tools like <b>ChatGPT</b>, <b>Claude</b>,
        and <b>Perplexity</b>.
      </p>
    </div>
  ),
  sov: (
    <div style={{ maxWidth: 320, lineHeight: "1.5em", fontSize: 12 }}>
      <b>Share of Voice (SOV)</b>
      <p>
        SOV shows how often your brand appears in AI-generated answers compared
        to competitors for a specific keyword.
      </p>

      <p>
        <b>Formula:</b> Your Mentions ÷ Total Mentions × 100
      </p>

      <p>
        <b>Higher SOV</b> = Stronger presence in AI responses
        <br />
        <b>Lower SOV</b> = Less visibility than competitors
      </p>

      <p>Displayed as a percentage (0–100%).</p>
    </div>
  ),
  visibility: (
    <div style={{ maxWidth: 320, lineHeight: "1.5em", fontSize: 12 }}>
      <b>Visibility Score</b>
      <p>
        Indicates how visible your domain is across AI platforms like{" "}
        <b>ChatGPT</b>, <b>Claude</b>, and <b>Perplexity</b>.
      </p>

      <p>
        Calculated using both <b>ranking position</b> and{" "}
        <b>number of brand mentions</b> in AI-generated content.
      </p>

      <p>
        <b>Higher score</b> = Higher visibility (frequent mentions + top
        positions)
        <br />
        <b>Lower score</b> = Rarely mentioned or low-ranked
      </p>

      <p>Displayed as a percentage (0–100%).</p>
    </div>
  ),
  sentiment: (
    <div style={{ maxWidth: 340, lineHeight: "1.5em", fontSize: 12 }}>
      <b>Sentiment Score</b>
      <p>
        Sentiment is based on how your brand is mentioned in AI-generated
        content.
      </p>

      <p>
        The score ranges from <b>0 to 100</b>, where:
        <br />• <b>0–30</b> = Negative – Mentions suggest criticism,
        dissatisfaction, or unfavorable tone.
        <br />• <b>31–60</b> = Neutral – Mentions are factual or balanced,
        without strong opinion.
        <br />• <b>61–100</b> = Positive – Mentions express praise, approval, or
        favorable tone.
      </p>

      <p>
        This helps you understand how your brand is being perceived across AI
        platforms.
      </p>
    </div>
  ),
};
